import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import './UserProfile.scss';

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const userData = await Auth.currentAuthenticatedUser();
      setUser(userData);
    } catch (err) {
      setError('Error fetching user data. Please sign in again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    // Check password complexity
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(passwordData.newPassword)) {
      setError('Password must be at least 8 characters long and include uppercase, lowercase, and numbers');
      return;
    }

    try {
      setLoading(true);
      await Auth.changePassword(
        user,
        passwordData.oldPassword,
        passwordData.newPassword
      );
      setMessage('Password changed successfully');
      setShowPasswordForm(false);
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      setError(err.message || 'Error changing password');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await Auth.signOut();
      window.location.reload();
    } catch (err) {
      setError('Error signing out');
    }
  };

  if (loading && !user) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="user-profile">
      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}

      {user && (
        <Card>
          <Card.Header as="h5">User Profile</Card.Header>
          <Card.Body>
            <div className="user-info">
              <p><strong>Email:</strong> {user.attributes?.email || 'Not available'}</p>
              {user.attributes?.given_name && (
                <p><strong>{user.attributes?.family_name && "First "}Name:</strong> {user.attributes.given_name}</p>
              )}
              {user.attributes?.family_name && (
                <p><strong>Last Name:</strong> {user.attributes.family_name}</p>
              )}
              {false && <p><strong>Account Created:</strong> {new Date(user.attributes?.sub ? parseInt(user.attributes.sub.substring(0, 8), 16) * 1000 : Date.now()).toLocaleDateString()}</p>}
            </div>

            <div className="mt-4">
              <Button 
                variant="primary" 
                onClick={() => setShowPasswordForm(!showPasswordForm)}
                className="me-2"
              >
                {showPasswordForm ? 'Cancel' : 'Change Password'}
              </Button>
              <Button variant="outline-danger" onClick={handleSignOut}>
                Sign Out
              </Button>
            </div>

            {showPasswordForm && (
              <Form onSubmit={handlePasswordSubmit} className="mt-4 password-form">
                <Form.Group className="mb-3">
                  <Form.Label>Current Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="oldPassword"
                    value={passwordData.oldPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                  <div className="text-muted small mt-1">
                    Password must be at least 8 characters long and include uppercase, lowercase, and numbers.
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Confirm New Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                  />
                </Form.Group>

                <Button type="submit" variant="success" disabled={loading}>
                  {loading ? 'Processing...' : 'Update Password'}
                </Button>
              </Form>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default UserProfile;
